// Which email addresses may claim a customer onboarding link (ENTRAID-347).
//
// The central IPC Global identity provider (repo ipcgs-cognito-stacks,
// lambda/provisioning/domain-policy.mjs) is the authority: its service invite
// refuses a staff SSO address and a freemail or consumer mailbox address with
// a 403. This module mirrors those two rules so the onboarding claim refuses
// such an address BEFORE the single-use link is consumed, with copy a buyer can
// act on, instead of burning the link on a provisioning call that can only
// fail. A difference between the two lists is never a hole: the central check
// still runs on every invite, and its 403 maps to the same refusal
// (lib/identity.mjs code 'address_refused').
//
// Keep both lists in step with the central repository:
//   - STAFF_EMAIL_DOMAINS: the central config ssoEmailDomains (lib/config.ts),
//     which the central IdP passes to its Lambdas as SSO_EMAIL_DOMAINS.
//   - FREEMAIL_DOMAINS / FREEMAIL_BRANDS: the central domain-policy.mjs lists.

/** IPC Global staff SSO domains; a subdomain of one is staff too. */
export const STAFF_EMAIL_DOMAINS = Object.freeze(['ipc-global.com']);

export const FREEMAIL_DOMAINS = Object.freeze([
  // Google
  'gmail.com', 'googlemail.com',
  // Microsoft
  'outlook.com', 'hotmail.com', 'hotmail.co.uk', 'hotmail.fr', 'hotmail.de', 'hotmail.it', 'hotmail.es',
  'live.com', 'live.co.uk', 'live.fr', 'msn.com', 'passport.com',
  // Yahoo and AOL
  'yahoo.com', 'yahoo.co.uk', 'yahoo.fr', 'yahoo.de', 'yahoo.it', 'yahoo.es', 'yahoo.com.br', 'yahoo.co.jp',
  'ymail.com', 'rocketmail.com', 'aol.com', 'aim.com',
  // Apple
  'icloud.com', 'me.com', 'mac.com',
  // Proton
  'proton.me', 'protonmail.com', 'protonmail.ch', 'pm.me',
  // Other large free providers
  'gmx.com', 'gmx.net', 'gmx.de', 'web.de', 'mail.com', 'email.com', 'zoho.com', 'zohomail.com',
  'yandex.com', 'yandex.ru', 'mail.ru', 'inbox.ru', 'list.ru', 'bk.ru',
  'tutanota.com', 'tutanota.de', 'tuta.io', 'tuta.com', 'hey.com', 'fastmail.com', 'fastmail.fm',
  'hushmail.com', 'mailfence.com', 'runbox.com', 'posteo.de', 'posteo.net',
  'qq.com', '163.com', '126.com', 'yeah.net', 'sina.com', 'naver.com', 'daum.net', 'hanmail.net',
  'rediffmail.com', 'libero.it', 'virgilio.it', 'orange.fr', 'wanadoo.fr', 'free.fr', 'laposte.net',
  'sfr.fr', 't-online.de', 'freenet.de', 'uol.com.br', 'bol.com.br', 'terra.com.br', 'ig.com.br',
  'sapo.pt', 'btinternet.com', 'sky.com', 'comcast.net', 'verizon.net', 'att.net', 'sbcglobal.net',
  'cox.net', 'charter.net', 'earthlink.net', 'optonline.net', 'shaw.ca', 'rogers.com', 'bigpond.com',
  // Country variants and regional providers
  'hotmail.com.br', 'hotmail.co.jp', 'hotmail.ca', 'hotmail.com.au', 'hotmail.nl', 'hotmail.be',
  'outlook.com.br', 'outlook.fr', 'outlook.de', 'outlook.es', 'outlook.it', 'outlook.pt', 'outlook.jp',
  'live.ca', 'live.de', 'live.it', 'live.nl', 'live.com.au', 'live.com.mx', 'live.com.pt', 'live.com.br',
  'yahoo.ca', 'yahoo.com.au', 'yahoo.com.mx', 'yahoo.com.ar', 'yahoo.co.in', 'yahoo.in', 'yahoo.co.id',
  'aol.co.uk', 'aol.de', 'aol.fr', 'gmx.at', 'gmx.ch', 'gmx.fr', 'gmx.co.uk', 'gmx.es', 'gmx.it', 'gmx.us',
  'oi.com.br', 'r7.com', 'globo.com', 'globomail.com', 'zipmail.com.br', 'terra.com', 'uol.com',
  'seznam.cz', 'wp.pl', 'o2.pl', 'onet.pl', 'interia.pl', 'abv.bg', 'rambler.ru', 'ukr.net', 'arcor.de',
  // Disposable-address services most often seen at sign-up
  'mailinator.com', 'guerrillamail.com', 'sharklasers.com', '10minutemail.com', 'temp-mail.org',
  'yopmail.com', 'trashmail.com', 'getnada.com', 'maildrop.cc', 'dispostable.com',
]);

/** Brands whose every `<brand>.<registry suffix>` domain is a consumer mailbox (hotmail.com.br, gmx.at). */
export const FREEMAIL_BRANDS = Object.freeze([
  'gmail', 'googlemail', 'hotmail', 'outlook', 'live', 'msn', 'windowslive', 'yahoo', 'ymail', 'rocketmail',
  'aol', 'gmx', 'yandex', 'protonmail', 'icloud',
]);

const FREEMAIL = new Set(FREEMAIL_DOMAINS);
const FREEMAIL_BRAND_SET = new Set(FREEMAIL_BRANDS);
// Generic second-level labels of country suffixes (co.uk, com.br, com.au).
const SECOND_LEVEL_LABELS = new Set([
  'co', 'com', 'net', 'org', 'gov', 'edu', 'ac', 'ltd', 'plc', 'me', 'gen', 'or', 'ne', 'go', 'gob', 'mil',
  'nic', 'info', 'biz', 'nom', 'ind', 'firm', 'web', 'lg', 'ad', 'gr', 'ed', 'sch', 'nhs', 'police',
]);

function isRegistrySuffix(domain) {
  const labels = domain.split('.');
  if (labels.length === 1) return true;
  return labels.length === 2 && labels[1].length === 2 && SECOND_LEVEL_LABELS.has(labels[0]);
}

/**
 * The lowercased part after the LAST '@' (the part mail is routed to), or ''.
 * Never the first '@': `x@acme.com@ipc-global.com` is an ipc-global.com
 * mailbox, the same reading the central IdP uses.
 */
export function emailDomain(email) {
  if (typeof email !== 'string') return '';
  const at = email.lastIndexOf('@');
  return at >= 0 ? email.slice(at + 1).trim().toLowerCase() : '';
}

const isDomainOrSubdomain = (domain, base) => domain === base || domain.endsWith(`.${base}`);

/** True for an IPC Global staff SSO domain or any subdomain of one. */
export function isStaffEmailDomain(domain) {
  if (typeof domain !== 'string' || !domain) return false;
  return STAFF_EMAIL_DOMAINS.some((s) => isDomainOrSubdomain(domain, s));
}

/** True for a freemail or consumer mailbox domain, or any subdomain of one. */
export function isFreemailDomain(domain) {
  if (typeof domain !== 'string' || !domain) return false;
  const labels = domain.split('.');
  for (let i = 0; i < labels.length - 1; i += 1) {
    if (FREEMAIL.has(labels.slice(i).join('.'))) return true;
    if (FREEMAIL_BRAND_SET.has(labels[i]) && isRegistrySuffix(labels.slice(i + 1).join('.'))) return true;
  }
  return false;
}

/**
 * Why `email` may not claim a customer onboarding link: 'staff' for an IPC
 * Global staff address, 'personal' for a freemail or consumer mailbox, or
 * null when this module has no objection (the central IdP still decides).
 */
export function claimAddressRefusal(email) {
  const domain = emailDomain(email);
  if (isStaffEmailDomain(domain)) return 'staff';
  if (isFreemailDomain(domain)) return 'personal';
  return null;
}
